import { useLayoutEffect, useRef } from 'preact/hooks'
import type MuxPlayerElement from '@mux/mux-player'
import { muxPlayerAttributes } from './source'
import type { RoomPlayerSource } from './source'
import type { Player } from './usePlayer'

/**
 * Fills its positioned parent. Mux Player's controls are all hidden (`--controls`): its control bar sits along the
 * bottom of the picture, where the client's title band is baked into the recording, so the room draws its own
 * controls top right or under the picture. Its loading spinner and error dialog stay, centred.
 */
const PLAYER_STYLE = 'display:block;position:absolute;inset:0;width:100%;height:100%;--controls:none'

interface RoomPlayerProps {
    player: Player
    source: RoomPlayerSource
    /** The class for the line that says the player's code didn't load, in the skin around it. */
    failureClass: string
}

/**
 * The room's one player: Mux Player, for the live stream, the replay, the rehearsal and the Room tab's preview. It
 * reports to `player`, which the controls around it read and drive. Mount it only when there is something it can
 * play: a live stream on the air (`streamOnAir`), or a recording with its token.
 */
export function RoomPlayer({ player, source, failureClass }: RoomPlayerProps) {
    const element = useRef<MuxPlayerElement>(null)
    const attributes = JSON.stringify(muxPlayerAttributes(source))

    useLayoutEffect(() => {
        const node = element.current
        if (!node) return
        for (const [name, value] of Object.entries(JSON.parse(attributes) as Record<string, string | null>)) {
            if (value === null) node.removeAttribute(name)
            else node.setAttribute(name, value)
        }
    }, [attributes])

    useLayoutEffect(() => {
        const node = element.current
        if (!node) return
        player.attach(node)
        return () => player.detach(node)
    }, [player])

    return (
        <>
            <mux-player ref={element} style={PLAYER_STYLE} />
            {player.media.value.status === 'failed' && (
                <p class={failureClass} role="alert">The video didn’t load. Reload the page to try again.</p>
            )}
        </>
    )
}
